import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { repository } from '../../data'
import type { InventoryItem, Receipt } from '../../data/inventory'
import { EmptyState } from '../primitives'
import { ReceiveDetails } from './ReceiveDetails'
import { ReceiveDone } from './ReceiveDone'
import { ReceivePick } from './ReceivePick'

/**
 * Receive — "22 cameras arrived", on a phone, in three screens:
 *
 *  1. **What arrived?** Search or tap a category; the most recently received
 *     come first, because a delivery is usually more of something.
 *  2. **How many** — one big number — and, for things with their own serial,
 *     optionally each maker's serial, scanned or typed one after another. Skip
 *     it and Kronos numbers them anyway. An optional note for the PO or slip.
 *  3. **Done**: "22 × Camera received · now 34 on the shelf", and the way back
 *     to the start.
 *
 * Nothing is asked that the receipt cannot use. A labelled item needs no label
 * in hand: Kronos allocates its numbers now and the label is printed later —
 * see the header of `packages/domain/src/inventory.ts`.
 */
type Step =
  | { readonly step: 'pick' }
  | { readonly step: 'details'; readonly itemId: string }
  | { readonly step: 'done'; readonly itemId: string; readonly receipt: Receipt & { readonly onShelf: number } }

export function ReceiveView({ initialItem }: { initialItem: string | null }): JSX.Element {
  const [state, setState] = useState<Step>(() => (initialItem ? { step: 'details', itemId: initialItem } : { step: 'pick' }))
  const items = useQuery({ queryKey: ['inventory'], queryFn: () => repository.inventory() })

  // The item in the address bar has done its job once it is read; leaving it
  // there would reopen it on every reload long after the box was put away.
  useEffect(() => {
    if (window.location.search.includes('item=')) window.history.replaceState(null, '', '/receive')
  }, [])
  // Each step starts at the top — on a phone the list scrolled away under it.
  useEffect(() => {
    document.querySelector('.shell-main')?.scrollTo({ top: 0 })
  }, [state.step])

  const find = (id: string): InventoryItem | undefined => items.data?.find((i) => i.id === id)

  if (items.isError) {
    return (
      <div className="page page--narrow">
        <EmptyState icon="receive" title="Could not load the items">
          Check the connection and try again.
        </EmptyState>
      </div>
    )
  }
  if (!items.data) return <div className="page page--narrow" />

  if (state.step === 'pick') {
    return <ReceivePick items={items.data} onPick={(item) => setState({ step: 'details', itemId: item.id })} />
  }

  const item = find(state.itemId)
  if (!item) {
    // A deep link to something that was retired, or a brand-new item the list
    // has not caught up with yet — the latter resolves itself on refetch.
    return <ReceivePick items={items.data} onPick={(i) => setState({ step: 'details', itemId: i.id })} />
  }

  if (state.step === 'details') {
    return (
      <ReceiveDetails
        key={item.id}
        item={item}
        onBack={() => setState({ step: 'pick' })}
        onDone={(receipt) => setState({ step: 'done', itemId: item.id, receipt })}
      />
    )
  }
  return (
    <ReceiveDone
      item={item}
      receipt={state.receipt}
      onAnother={() => setState({ step: 'pick' })}
      onMore={() => setState({ step: 'details', itemId: item.id })}
    />
  )
}
